/**
 * File list and picker for Sitecore Forms File Upload fields.
 *
 * The native input stays in the form so the browser still posts the files.
 * Chosen files are kept here and written back with DataTransfer, which is
 * what lets a visitor remove one file without clearing the rest. A later
 * pick is added to files already chosen when the field allows
 * multiple files. Files are chosen with the Upload Files button only.
 */

const selections = new WeakMap();
let syncing = false;

const IMAGE = { color: "#188038", image: true };
const WORD = { color: "#185abc", label: "DOC" };
const SHEET = { color: "#188038", label: "XLS" };
const TEXT = { color: "#f9ab00", label: "TXT" };
const MARKUP = { color: "#c2410c", label: "XML" };

const FILE_TYPES = {
  pdf: { color: "#d93025", label: "PDF" },
  doc: WORD,
  docx: WORD,
  dot: { color: "#185abc", label: "DOT" },
  rtf: { color: "#185abc", label: "RTF" },
  xls: SHEET,
  xlsx: SHEET,
  xlst: SHEET,
  csv: { color: "#188038", label: "CSV" },
  txt: TEXT,
  text: TEXT,
  xml: MARKUP,
  xbl: { color: "#c2410c", label: "XBL" },
  xsl: { color: "#c2410c", label: "XSL" },
  msg: { color: "#0078d4", label: "MSG" },
  png: IMAGE,
  jpg: IMAGE,
  jpe: IMAGE,
  jpeg: IMAGE,
  jfif: IMAGE,
  pjpeg: IMAGE,
  pjp: IMAGE,
  tif: IMAGE,
  tiff: IMAGE,
};

function fileKey(file) {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

function extensionOf(name) {
  const parts = String(name || "").split(".");
  if (parts.length < 2) return "";
  return parts.pop().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function fileType(name) {
  const ext = extensionOf(name);
  if (FILE_TYPES[ext]) return FILE_TYPES[ext];
  const label = (ext || "file").slice(0, 4).toUpperCase();
  return { color: "#5f6368", label };
}

function svgEl(name, attrs) {
  const el = document.createElementNS("http://www.w3.org/2000/svg", name);
  Object.keys(attrs).forEach((key) => el.setAttribute(key, attrs[key]));
  return el;
}

function createTypeIcon(fileName) {
  const type = fileType(fileName);
  const svg = svgEl("svg", {
    class: "file-upload-selection__icon",
    viewBox: "0 0 26 32",
    width: "26",
    height: "32",
    "aria-hidden": "true",
    focusable: "false",
  });

  svg.appendChild(
    svgEl("path", {
      fill: type.color,
      d: "M2 0h14l10 10v18a4 4 0 0 1-4 4H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z",
    }),
  );
  svg.appendChild(
    svgEl("path", {
      fill: "rgba(255,255,255,0.55)",
      d: "M16 0v7a3 3 0 0 0 3 3h7z",
    }),
  );

  if (type.image) {
    svg.appendChild(
      svgEl("path", {
        fill: "#fff",
        d: "M5 22l4.2-5 3 3.4L16.5 15 21 22H5zm3.2-8.2a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2z",
      }),
    );
  } else {
    const text = svgEl("text", {
      x: "13",
      y: "23",
      "text-anchor": "middle",
      fill: "#fff",
      "font-size": type.label.length > 3 ? "6" : "7",
      "font-family": "Arial, Helvetica, sans-serif",
      "font-weight": "700",
    });
    text.textContent = type.label;
    svg.appendChild(text);
  }

  return svg;
}

function createRemoveIcon() {
  const svg = svgEl("svg", {
    class: "file-upload-selection__remove-icon",
    viewBox: "0 0 24 24",
    width: "16",
    height: "16",
    "aria-hidden": "true",
    focusable: "false",
  });
  svg.appendChild(
    svgEl("path", {
      fill: "currentColor",
      d: "M18.3 5.7a1 1 0 0 0-1.4 0L12 10.6 7.1 5.7a1 1 0 0 0-1.4 1.4L10.6 12l-4.9 4.9a1 1 0 1 0 1.4 1.4L12 13.4l4.9 4.9a1 1 0 0 0 1.4-1.4L13.4 12l4.9-4.9a1 1 0 0 0 0-1.4z",
    }),
  );
  return svg;
}

function getList(input) {
  const wrapper = input.closest(".forms-file-upload") || input.parentElement;
  if (!wrapper) return null;

  let list = wrapper.querySelector("[data-file-upload-selection]");
  if (!list) {
    list = document.createElement("ul");
    list.className = "file-upload-selection";
    list.setAttribute("data-file-upload-selection", "");
    list.hidden = true;
    wrapper.insertBefore(list, wrapper.firstChild);
  }
  return list;
}

function applyFiles(input, files) {
  selections.set(input, files);
  render(input, files);
  syncing = true;
  try {
    if (files.length === 0) {
      input.value = "";
    } else if (typeof DataTransfer !== "undefined") {
      const transfer = new DataTransfer();
      files.forEach((file) => transfer.items.add(file));
      input.files = transfer.files;
    }
  } catch (error) {
    // The list is already drawn. A browser that rejects the rewrite
    // must not leave the visitor with names and no remove button.
  } finally {
    syncing = false;
  }
}

function commitIncoming(input, incoming) {
  const files = Array.from(incoming || []);
  if (!input.multiple) {
    applyFiles(input, files.slice(0, 1));
    return;
  }

  const existing = selections.get(input) || [];
  const seen = new Set(existing.map(fileKey));
  const merged = existing.slice();

  files.forEach((file) => {
    const key = fileKey(file);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(file);
  });

  applyFiles(input, merged);
}

function render(input, files) {
  const list = getList(input);
  if (!list) return;

  list.textContent = "";

  if (!files.length) {
    list.hidden = true;
    return;
  }

  list.hidden = false;

  files.forEach((file, index) => {
    const item = document.createElement("li");
    item.className = "file-upload-selection__item";

    const name = document.createElement("span");
    name.className = "file-upload-selection__name";
    name.textContent = file.name;
    name.title = file.name;

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "file-upload-selection__remove";
    remove.setAttribute("data-file-upload-remove", "");
    remove.dataset.fileIndex = String(index);
    remove.setAttribute("aria-label", `Remove ${file.name}`);
    remove.appendChild(createRemoveIcon());

    item.append(createTypeIcon(file.name), name, remove);
    list.appendChild(item);
  });
}

function fieldInput(node) {
  const wrapper = node && node.closest(".forms-file-upload");
  return wrapper && wrapper.querySelector("input.js-forms-file-input");
}

function onChange(event) {
  const input = event.target;
  if (!input || !input.matches || !input.matches("input.js-forms-file-input")) {
    return;
  }
  if (syncing) return;
  if (input.closest(".forms-file-upload")?.querySelector("[data-file-upload-selection]")?.getAttribute("data-file-selection-inline") === "true") {
    return;
  }
  const incoming = Array.from(input.files || []);
  const alreadyShown = selections.get(input) || [];
  // A dismissed picker reports an empty FileList. The names stay on screen,
  // so the kept files have to be written back or the next post would drop them.
  if (!incoming.length && alreadyShown.length) {
    applyFiles(input, alreadyShown);
    return;
  }
  commitIncoming(input, incoming);
}

function onPickerClick(event) {
  const input = event.target;
  if (!input || !input.matches || !input.matches("input.js-forms-file-input")) {
    return;
  }
  // The auth gate calls preventDefault before this listener. Leave that
  // input alone so a blocked picker does not wipe files the form will post.
  if (event.defaultPrevented || syncing) return;

  // Chrome does not fire change for a file it already has on this input.
  // After every chosen file is removed, that includes the next choice, same
  // name or not, until the page is reloaded. Clearing here, on the click that
  // opens the picker, makes the next choice a change. Kept files stay in
  // memory and are restored if the visitor cancels.
  syncing = true;
  try {
    input.value = "";
  } finally {
    syncing = false;
  }
}

function onPickerCancel(event) {
  const input = event.target;
  if (!input || !input.matches || !input.matches("input.js-forms-file-input")) {
    return;
  }
  if (syncing) return;
  const ownedList = input.closest(".forms-file-upload")?.querySelector("[data-file-upload-selection]");
  if (ownedList && ownedList.getAttribute("data-file-selection-inline") === "true") return;

  const kept = selections.get(input) || [];
  if (!kept.length || (input.files && input.files.length)) return;
  applyFiles(input, kept);
}

function onRemove(event) {
  const button = event.target.closest("[data-file-upload-remove]");
  if (!button) return;
  const ownedList = button.closest("[data-file-upload-selection]");
  if (ownedList && ownedList.getAttribute("data-file-selection-inline") === "true") return;

  const input = fieldInput(button);
  if (!input) return;

  event.preventDefault();

  const index = Number(button.dataset.fileIndex);
  const current = selections.get(input) || [];
  applyFiles(
    input,
    current.filter((_, fileIndex) => fileIndex !== index),
  );
}

export function initFileUploadSelection() {
  if (window.__threadFileUploadSelectionWired) return;
  window.__threadFileUploadSelectionWired = true;

  document.addEventListener("click", onPickerClick, true);
  document.addEventListener("cancel", onPickerCancel, true);
  document.addEventListener("change", onChange);
  document.addEventListener("click", onRemove);
}
